import { GAME_CONFIG, ndviTotalSec } from '../config/gameConfig';
import type { CommandTool, Field, GameState } from '../state/types';

export type DispatchFn = (fieldId: number, forceUnit?: CommandTool | null) => void;

const FOOTER_TIP =
  '💡 Vidd az egeret a mező fölé, kattints, majd válassz <b>Felmérést</b> vagy <b>Permetezést</b>.';

const ROW_ALIVE =
  'p-3 rounded-xl border flex flex-col transition duration-300 bg-slate-900/50 border-slate-800/80';
const ROW_DEAD =
  'p-3 rounded-xl border flex flex-col transition duration-300 bg-red-950/20 border-red-900/50';

const FIELD_BTN_IDLE =
  'flex-1 py-1 px-2 rounded-lg text-[9px] font-bold tracking-wider transition duration-300 border bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:border-emerald-500/50';
const FIELD_BTN_ACTIVE =
  'flex-1 py-1 px-2 rounded-lg text-[9px] font-bold tracking-wider transition duration-300 border bg-emerald-500/30 text-emerald-300 border-emerald-500/50 shadow-lg shadow-emerald-500/10';
const DRONE_BTN_IDLE = `field-btn-drone ${FIELD_BTN_IDLE}`;
const DRONE_BTN_ACTIVE = `field-btn-drone ${FIELD_BTN_ACTIVE}`;
const TRUCK_BTN_IDLE = `field-btn-truck ${FIELD_BTN_IDLE}`;
const TRUCK_BTN_ACTIVE = `field-btn-truck ${FIELD_BTN_ACTIVE}`;

let currentDispatch: DispatchFn | null = null;
let footerInitialized = false;
let delegationBound = false;

function bindListDelegation(listContainer: HTMLElement): void {
  if (delegationBound) return;
  delegationBound = true;

  listContainer.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target || !listContainer.contains(target)) return;

    const action = target.dataset.action;
    const row = target.closest<HTMLElement>('[data-field-id]');
    if (!row || !currentDispatch) return;

    const fieldId = Number(row.dataset.fieldId);
    if (!Number.isFinite(fieldId)) return;

    e.stopPropagation();
    if (action === 'drone' || action === 'truck') {
      currentDispatch(fieldId, action);
    }
  });
}

function ensureFieldRow(listContainer: HTMLElement, field: Field): HTMLElement {
  let row = listContainer.querySelector<HTMLElement>(`[data-field-id="${field.id}"]`);
  if (row) return row;

  row = document.createElement('div');
  row.dataset.fieldId = String(field.id);
  row.className = ROW_ALIVE;

  const topRow = document.createElement('div');
  topRow.className = 'flex justify-between items-center w-full';

  const leftSide = document.createElement('div');
  leftSide.className = 'flex flex-col flex-1 min-w-0';

  const nameSpan = document.createElement('span');
  nameSpan.className = 'font-bold text-xs text-slate-100 flex items-center gap-1.5';

  const statusDot = document.createElement('span');
  statusDot.className = 'field-status-dot h-2 w-2 rounded-full bg-emerald-500';
  nameSpan.appendChild(statusDot);
  nameSpan.appendChild(document.createTextNode(`🌾 ${field.name}`));
  leftSide.appendChild(nameSpan);

  const ndviLabel = document.createElement('span');
  ndviLabel.className = 'field-ndvi-label text-[10px] text-slate-400 mt-1';
  ndviLabel.textContent = 'NDVI érvényesség';
  leftSide.appendChild(ndviLabel);

  const barTrack = document.createElement('div');
  barTrack.className =
    'field-ndvi-track mt-1 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden';
  const barFill = document.createElement('div');
  barFill.className =
    'field-ndvi-bar h-full rounded-full bg-cyan-400 transition-[width] duration-75';
  barFill.style.width = '0%';
  barTrack.appendChild(barFill);
  leftSide.appendChild(barTrack);

  topRow.appendChild(leftSide);

  const rightSide = document.createElement('div');
  rightSide.className = 'text-right flex flex-col items-end ml-2';

  const statusSpan = document.createElement('span');
  statusSpan.className = 'field-status-text text-[11px] font-bold text-slate-500';
  rightSide.appendChild(statusSpan);

  const unitStatus = document.createElement('span');
  unitStatus.className =
    'field-unit-status hidden text-[9px] bg-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded-full mt-1 font-mono';
  rightSide.appendChild(unitStatus);

  topRow.appendChild(rightSide);
  row.appendChild(topRow);

  const btnContainer = document.createElement('div');
  btnContainer.className = 'field-actions flex gap-2 mt-2.5 w-full';

  const droneBtn = document.createElement('button');
  droneBtn.type = 'button';
  droneBtn.dataset.action = 'drone';
  droneBtn.className = DRONE_BTN_IDLE;
  droneBtn.textContent = '🚁 Felmérés';

  const truckBtn = document.createElement('button');
  truckBtn.type = 'button';
  truckBtn.dataset.action = 'truck';
  truckBtn.className = TRUCK_BTN_IDLE;
  truckBtn.textContent = '🚗 Permetezés';

  btnContainer.appendChild(droneBtn);
  btnContainer.appendChild(truckBtn);
  row.appendChild(btnContainer);

  listContainer.appendChild(row);
  return row;
}

function patchFieldRow(row: HTMLElement, field: Field, state: GameState): void {
  const droneActive = state.drone.targetFieldId === field.id;
  const truckActive = state.truck.targetFieldId === field.id;
  const total = ndviTotalSec();
  const pct = total > 0 ? Math.max(0, Math.min(100, (field.ndviRemaining / total) * 100)) : 0;
  const fading = field.ndviRemaining > 0 && field.ndviRemaining <= GAME_CONFIG.fields.ndviFadeSec;

  row.className = field.dead ? ROW_DEAD : ROW_ALIVE;

  const statusDot = row.querySelector('.field-status-dot');
  if (statusDot) {
    statusDot.className = `field-status-dot h-2 w-2 rounded-full ${
      field.dead ? 'bg-red-500' : 'bg-emerald-500'
    }`;
  }

  const barFill = row.querySelector<HTMLElement>('.field-ndvi-bar');
  if (barFill) {
    barFill.style.width = `${pct}%`;
    barFill.className = `field-ndvi-bar h-full rounded-full transition-[width] duration-75 ${
      field.ndviRemaining <= 0 ? 'bg-slate-600' : fading ? 'bg-amber-400' : 'bg-cyan-400'
    }`;
  }

  const statusSpan = row.querySelector('.field-status-text');
  if (statusSpan) {
    if (field.dead) {
      statusSpan.className = 'field-status-text text-[11px] font-bold text-red-500';
      statusSpan.textContent = 'CROP ELHALT';
    } else if (field.ndviRemaining <= 0) {
      statusSpan.className = 'field-status-text text-[11px] font-bold text-slate-500';
      statusSpan.textContent = 'Nincs NDVI';
    } else if (fading) {
      statusSpan.className = 'field-status-text text-[11px] font-bold text-amber-400';
      statusSpan.textContent = 'Halványodik';
    } else {
      statusSpan.className = 'field-status-text text-[11px] font-bold text-cyan-400';
      statusSpan.textContent = 'Érvényes';
    }
  }

  const unitStatus = row.querySelector('.field-unit-status');
  if (unitStatus) {
    if (droneActive || truckActive) {
      unitStatus.classList.remove('hidden');
      unitStatus.textContent = droneActive
        ? `🚁 DRÓN (${state.drone.state})`
        : `🚗 PICKUP (${state.truck.state})`;
    } else {
      unitStatus.classList.add('hidden');
      unitStatus.textContent = '';
    }
  }

  const actions = row.querySelector('.field-actions');
  if (actions) {
    actions.classList.toggle('hidden', !!field.dead);
  }

  const droneBtn = row.querySelector('.field-btn-drone');
  if (droneBtn) {
    const active = droneActive && state.drone.state !== 'Idle';
    droneBtn.className = active ? DRONE_BTN_ACTIVE : DRONE_BTN_IDLE;
  }

  const truckBtn = row.querySelector('.field-btn-truck');
  if (truckBtn) {
    const active = truckActive && state.truck.state !== 'Idle';
    truckBtn.className = active ? TRUCK_BTN_ACTIVE : TRUCK_BTN_IDLE;
  }
}

export function updateHTML_UI(state: GameState, onDispatch: DispatchFn): void {
  currentDispatch = onDispatch;

  const cashDisplay = document.getElementById('cash-display');
  if (cashDisplay) {
    cashDisplay.textContent = `$${state.cash}`;
  }

  const listContainer = document.getElementById('fields-list');
  if (!listContainer) return;

  bindListDelegation(listContainer);

  state.fields.forEach((f) => {
    const row = ensureFieldRow(listContainer, f);
    patchFieldRow(row, f, state);
  });

  const truckStatus = document.getElementById('truck-status');
  if (truckStatus) {
    truckStatus.textContent = `Státusz: ${
      state.truck.state === 'Idle' ? 'Várakozik' : `${state.truck.state} (${state.truck.task})`
    }`;
  }

  const droneStatus = document.getElementById('drone-status');
  if (droneStatus) {
    droneStatus.textContent = `Státusz: ${
      state.drone.state === 'Idle' ? 'Várakozik' : `${state.drone.state} (${state.drone.task})`
    }`;
  }

  const footerText = document.getElementById('sidebar-footer');
  if (footerText && !footerInitialized) {
    footerInitialized = true;
    footerText.innerHTML = FOOTER_TIP;
  }
}
